import {
    getDomainName,
    listDomainNames,
    lookupHostname
} from './queries';
import {
    createDomainName,
    deleteDomainName,
    updateDomainName,
} from './mutations';

import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';
import { DomainName } from "../types.ts";
import {
    showErrorNotifications,
    showSuccessNotification
} from "../../../components/ServiceNotifications.tsx";


// FNP-1282: the WHOLE connection, not just `items`. The previous shape had no
// `nextToken` member, which is what made the service throw the token away at the
// return statement even though `queries.ts` selects it.
export type DomainNamesConnectionResult = {
    items: DomainName[],
    nextToken?: string | null,
}

export type ListDomainNames = {
    listDomainNames: DomainNamesConnectionResult
}

export type GetDomainName = {
    getDomainName: DomainName
}

export type CreateDomainName = {
    createDomainName: DomainName
}

export type UpdateDomainName = {
    updateDomainName: DomainName
}

export type DeleteDomainName = {
    deleteDomainName: string
}

// Client will be generated when needed to allow for proper mocking
const getClient = () => generateClient();

// Page size is a SERVER concern under token pagination — fixed by the module,
// never a "rows per page" dropdown.
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Fetches one PAGE of domain name records.
 *
 * The backend GraphQL resolvers handle all user isolation filtering automatically
 * based on the authenticated user's context, so the filter is passed through
 * unchanged.
 *
 * @param filter - Filter object passed unchanged to the backend, which combines it
 *                 with user-based filtering.
 * @param limit - Page size. A SERVER concern under token pagination — fixed by the
 *                module, never a "rows per page" dropdown.
 * @param nextToken - Cursor for the page to fetch; `null` for the first page.
 * @param userId - DEPRECATED: no longer used. Backend handles user isolation.
 * @returns The whole connection, so the caller can page.
 */
export const fetchRecords = async (
    filter: object,
    limit: number = DEFAULT_PAGE_SIZE,
    nextToken: string | null = null,
    userId?: string,
): Promise<DomainNamesConnectionResult> => {
    // Show deprecation warning if userId is provided
    if (userId !== undefined) {
        console.warn('fetchRecords: userId parameter is deprecated and no longer used. Backend now handles user isolation filtering automatically.');
    }

    try {
        const response = await getClient().graphql<GraphQLQuery<ListDomainNames>>({
            query: listDomainNames,
            variables: {
                filter,
                limit,
                nextToken,
            },
        });
        return {
            items: response.data?.listDomainNames?.items ?? [],
            // ⚠️ `?? null` and NOT `|| null`: coercing an empty-string token to
            // null would end pagination early.
            nextToken: response.data?.listDomainNames?.nextToken ?? null,
        };
    } catch (e) {
        console.debug(e);
        // FNP-1282: the toast still fires, but the rejection is RE-THROWN rather
        // than swallowed. Returning undefined here meant the query never entered
        // an error state, so a failed load was indistinguishable from an empty
        // tenant. React Query needs the rejection to set `isError`.
        showErrorNotifications(e)
        throw e;
    }
};

export const fetch = async (id: string) => {
    try {
        const response = await getClient().graphql<GraphQLQuery<GetDomainName>>({
            query: getDomainName,
            variables: { id: id },
        });
        return response.data?.getDomainName;
    } catch (e) {
        showErrorNotifications(e)
    }
};

/**
 * The fields the API accepts on a write.
 *
 * ⚠️ FNP-1282 / FNP-996: this exists so the mutation payload is built by
 * WHITELIST rather than by deleting keys off whatever the caller handed over.
 * `created`, `updated`, `createdBy`, `registrar` and `topLevelDomain` are all
 * server-owned or resolved sub-objects and must never be sent back.
 */
const WRITABLE_FIELDS = [
    'name',
    'tldId',
    'category',
    'purchaseDate',
    'expirationDate',
    'registrarId',
] as const;

/**
 * Copies the writable fields into a NEW object.
 *
 * ⚠️ Never mutate the argument. On the edit path the caller passes its live
 * Formik `values` object straight through; `delete record.created` used to strip
 * fields off the form's own state, which is the FNP-996 defect verbatim.
 * `undefined` fields are omitted so an untouched field is not sent as null.
 */
const toWritablePayload = (record: Partial<DomainName>): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const field of WRITABLE_FIELDS) {
        const value = record[field];
        if (typeof value !== 'undefined') {
            payload[field] = value;
        }
    }
    return payload;
};

export const add = async (record: Partial<DomainName>) => {

    try {
        // Backend automatically extracts user context from authentication token
        // No need to pass createdByUsername explicitly
        const response = await getClient().graphql<GraphQLQuery<CreateDomainName>>({
            query: createDomainName,
            variables: { input: toWritablePayload(record) },
        });
        console.debug('in the service response', response);

        showSuccessNotification();
        return response.data?.createDomainName;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const update = async (id: string, record: DomainName) => {

    console.debug('IN UPDATE DOMAIN NAME', id, record);

    // ⚠️ The payload is BUILT, not edited. `record` is the caller's live Formik
    // values object — see toWritablePayload.
    const input = {
        id,
        ...toWritablePayload(record),
    };

    try {
        const response = await getClient().graphql<GraphQLQuery<UpdateDomainName>>({
            query: (updateDomainName),
            variables: { input },
        });

        showSuccessNotification();
        return response.data?.updateDomainName;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await getClient().graphql<GraphQLQuery<DeleteDomainName>>({
            query: (deleteDomainName),
            variables: { input: { id: id } },

        });
        showSuccessNotification();
        return response.data?.deleteDomainName;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const lookupDnsInformation = async (hostname: string) => {
    try {
        // FNP-960: only .lookupHostname is read off the response, and its shape is
        // pinned by the consumer in domain-names/pages/EditPage.tsx, which feeds
        // it to a useState<{address, family} | null>.
        const response = await getClient().graphql<GraphQLQuery<{ lookupHostname: { address: string, family: number } | null }>>({
            query: (lookupHostname),
            variables: {
                hostname: hostname
            },

        });
        showSuccessNotification('DNS Lookup Successful');
        return response.data?.lookupHostname;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};
